import React, { useState, useEffect } from 'react';
import { IoChatbubbleOutline, IoChatbubblesOutline } from "react-icons/io5";
import { useFilePicker } from 'use-file-picker';
import { FileAmountLimitValidator, FileSizeValidator } from 'use-file-picker/validators';
import { useDatabase } from '../contexts/DatabaseContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './primitives';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import PageParrotFinal from '../assets/PageParrotFinal.svg';

import { FaUpload } from "react-icons/fa";
import { GoCircleSlash } from "react-icons/go";
import { LuTrash2 } from "react-icons/lu";

export default function WorkspaceDesignTab({
    icon,
    workspaceId,
    setConfig,
    workspaceType,
    selectedColor,
    setSelectedColor,
    customIconUrl,
    setCustomIconUrl,
    iconFile,
    setIconFile,
    workspaceStyles,
    setWorkspaceStyles,
    hasChangesDesign,
    requestedVisualTrigger,
}) {
    const COLOR_PRESETS = [
        { 
            name: "Rainforest Canopy",
            mainColor: "#27ca52",
            V1: "linear-gradient(-45deg, #1B5E20, #00C853)", /* Chat Launcher */
            V2: "linear-gradient(0deg, #1F7D5A, #27CA52)", /* Chat Header */
            V3: "linear-gradient(-5deg, #1B5E20, #00C853)", /* Visitor Message */
            V4: "linear-gradient(0deg, #1B5E20, #3DBE6C)", /* Chat Footer */
            V5: "linear-gradient(#27ca52, #10A838)",
        },
        { 
            name: "Ocean Mist",
            mainColor: "#2196F3",
            V1: "linear-gradient(-45deg, #0D47A1, #2196F3)", /* Chat Launcher */
            V2: "linear-gradient(0deg, #1565C0, #42A5F5)", /* Chat Header */
            V3: "linear-gradient(-5deg, #0D47A1, #2196F3)", /* Visitor Message */
            V4: "linear-gradient(0deg, #0D47A1, #64B5F6)", /* Chat Footer */
            V5: "linear-gradient(#2196F3, #1976D2)",
        },
        { 
            name: "Sunset Tropics",
            mainColor: "#FF5722",
            V1: "linear-gradient(-45deg, #BF360C, #FF9800)", /* Chat Launcher */
            V2: "linear-gradient(0deg, #E64A19, #FFAB40)", /* Chat Header */
            V3: "linear-gradient(-5deg, #D84315, #FF7043)", /* Visitor Message */
            V4: "linear-gradient(0deg, #BF360C, #FFB74D)", /* Chat Footer */
            V5: "linear-gradient(#FF5722, #E64A19)",
        },
        { 
            name: "Parrot Plumage",
            mainColor: "#4CAF50",
            V1: "linear-gradient(-45deg, #1565C0, #4CAF50)", /* Chat Launcher */
            V2: "linear-gradient(0deg, #D32F2F, #FFEB3B)", /* Chat Header */
            V3: "linear-gradient(-5deg, #388E3C, #42A5F5)", /* Visitor Message */
            V4: "linear-gradient(0deg, #7B1FA2, #26A69A)", /* Chat Footer */
            V5: "linear-gradient(#4CAF50, #1976D2)",
        },
    ];
    
    const ICON_OPTIONS = [
        { name: 'default', value: 'default', icon: <img src={PageParrotFinal} alt="PageParrot" className="grid-icon" /> },
        { name: 'Chat1', value: 'chat1', icon: <IoChatbubbleOutline className="grid-icon" /> },
        { name: 'Chat2', value: 'chat2', icon: <IoChatbubblesOutline className="grid-icon" /> },
    ];

    const [loading, setLoading] = useState(false);

    const { uploadFile, deleteFile, updateWorkspaceConfigDoc, createDesignActionEvent } = useDatabase();
    const { currentUser } = useAuth();
    const { mobile } = useOutletContext();

    const t = useTranslation();

    const { openFilePicker, loading: filePickerLoading, errors: filePickerErrors } = useFilePicker({
        multiple: false,
        accept: ['image/*'],
        validators: [
            new FileAmountLimitValidator({ max: 1 }),
            new FileSizeValidator({ maxFileSize: 3000000 }),
        ],
        onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
            console.log("Files selected");
            try {
                if (!filePickerLoading) {
                    setIconFile(plainFiles[0]);
                    setCustomIconUrl(URL.createObjectURL(plainFiles[0]));
                    const allSettings = ['iconSetting1', 'iconSetting2', 'iconSetting3']; //iconSetting4
                    allSettings.forEach(settingKey => {
                        updateSetting(settingKey, { 
                            iconSetting: 'upload', 
                            selectedIcon: workspaceStyles[settingKey]?.selectedIcon || 'default' 
                        });
                    });
                }
            } catch (e) {
                console.error(e);
            }
        },
        onClear: () => {
            console.log("File cleared");
        }
    });

    useEffect(() => {
        if (filePickerErrors.length > 0) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: filePickerErrors[0].message
                }
            }));
        }
    }, [filePickerErrors]);

    const updateSetting = (key, value) => {
        setWorkspaceStyles(prev => {
            return {
                ...prev,
                [key]: value
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            let updatedStyles = {
                ...workspaceStyles,
                colors: selectedColor,
            };
            if (updatedStyles.uploadCount >= 50) {
                throw new Error('You have reached the maximum number of custom icons for this workspace, please reach out to support if you need further assistance.');
            }
            console.log('Selected Design:', updatedStyles);
            if (iconFile) {
                console.log('Uploading File:', iconFile);
                const updatedFile = new File([iconFile], `${currentUser.uid}-${iconFile.name}`, { type: iconFile.type });
                const oldIconUrl = updatedStyles.customIcon;
                const resultURL = await uploadFile(updatedFile);
                updatedStyles.customIcon = resultURL;
                if (oldIconUrl !== null) { await deleteFile(oldIconUrl); }
                workspaceStyles.customIcon = resultURL;
            }
            updatedStyles.uploadCount = updatedStyles.uploadCount ? updatedStyles.uploadCount + 1 : 1;
            await updateWorkspaceConfigDoc(workspaceId, { workspaceStyles: updatedStyles });
            setConfig(prev => ({ ...prev, workspaceStyles: updatedStyles }));
            setIconFile(null);
            createDesignActionEvent(
                workspaceType,
                workspaceId,
                selectedColor.name,
                updatedStyles.iconSetting1.iconSetting === "choose" ? updatedStyles.iconSetting1.selectedIcon : updatedStyles.iconSetting1.iconSetting,
                updatedStyles.iconSetting2.iconSetting === "choose" ? updatedStyles.iconSetting2.selectedIcon : updatedStyles.iconSetting2.iconSetting,
                updatedStyles.iconSetting3.iconSetting === "choose" ? updatedStyles.iconSetting3.selectedIcon : updatedStyles.iconSetting3.iconSetting,
                iconFile ? true : false,
            );
        } catch (e) {
            console.error(e);
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: e.message,
                    action: "design",
                }
            }));
        } finally {
            setLoading(false);
        }
    };

    const deleteIcon = () => {
        setIconFile(null);
        setCustomIconUrl(null);
    };

    const props = {
        config: workspaceStyles,
        updateSetting,
        openFilePicker,
        customIconUrl,
        icon,
        deleteIcon,
        ICON_OPTIONS,
        COLOR_PRESETS,
    };

    return (
        <div>
        {/* <Card className="white">
             <Card.Body> */}
                <div>
                    <h2 className="mb-4">{t('Chat App Design')}</h2>
                    <div className="design-title-container">
                        <span className="design-title">{t('Theme Selector')}</span><br/>
                        <span className="design-description">{t('These color palettes will be applied to the chat window')}</span>
                    </div>
                    <div className="color-preset-row">
                        {COLOR_PRESETS.map((preset, index) => (
                            <div
                                key={index}
                                className={`color-preset${selectedColor?.name === preset?.name ? ' selected' : ''}${mobile ? ' mobile' : ''}`}
                                onClick={() => setSelectedColor(preset)}
                            >
                                <div className="color-preset-inner"
                                    style={{ background: `${preset.V1}` }}
                                />
                            </div>
                        ))}
                    </div>
                    <br/>
                    <IconSettingComponent 
                        settingKey="iconSetting1"
                        settingName={t('Main Bubble Icon')}
                        settingDescription={t('This icon will be displayed to visitors who have not yet opened the chat window')}
                        includeOffOption={false}
                        onClick={() => requestedVisualTrigger.current = 'bubble'}
                        {...props}
                    />
                    <br/>
                    <IconSettingComponent 
                        settingKey="iconSetting2" 
                        settingName={t('Popup Icon')}
                        settingDescription={t('This icon will be displayed accompanying your welcome message')}
                        onClick={() => requestedVisualTrigger.current = 'popup'}
                        {...props}
                    />
                    <br/>
                    <IconSettingComponent 
                        settingKey="iconSetting3" 
                        settingName={t('Chat Title Icon')}
                        settingDescription={t('This icon will be displayed in the chat title bar')}
                        onClick={() => requestedVisualTrigger.current = 'chat'}
                        {...props}
                    />
                    <br/>
                    {/* <IconSettingComponent 
                        settingKey="iconSetting4"
                        settingDescription={t('This setting\'s purpose has yet to be determined')}
                        {...props}
                    />
                    <br/> */}
                    <Button
                        onClick={handleSubmit}
                        className="submit settings"
                        disabled={!hasChangesDesign}
                        loading={loading}
                        
                    >
                        {t('Save Design')}
                    </Button>
                </div>
             {/* </Card.Body>
         </Card> */}
        </div>
    );
}

const IconSettingComponent = ({ ICON_OPTIONS, settingKey, config, updateSetting, openFilePicker, customIconUrl, icon, includeOffOption = true, deleteIcon, settingName = null, settingDescription = null, onClick = null }) => {
    const settingConfig = config?.[settingKey] || { iconSetting: 'choose', selectedIcon: 'default' };
    const [hovering, setHovering] = useState(false);
    const { mobile } = useOutletContext();
    const t = useTranslation();
    const { iconSetting, selectedIcon } = settingConfig;

    const handleSettingChange = (newSetting, newIcon = selectedIcon) => {
        updateSetting(settingKey, { iconSetting: newSetting, selectedIcon: newIcon });
    };

    return (
        <div className="icon-setting-container" onClick={onClick}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="design-title-container">
                    <span className="design-title">{settingName || `Icon Setting ${settingKey.slice(-1)}`}</span><br/>
                    <span className="design-description" style={{ marginTop: '50px' }}>{settingDescription}</span>
                </div>
                {!mobile && (
                    <div style={{ width: '20%', display: 'flex', justifyContent: 'center' }}>
                        <button 
                            className="upload-button" 
                            onClick={() => {
                                openFilePicker();
                            }}
                        >
                            {t('Upload Icon')}  
                        </button>
                    </div>
                )}
            </div>
            
            <div className="icon-grid">
                {ICON_OPTIONS.map((preset) => (
                    <div
                        key={preset.name}
                        className={`grid-item${iconSetting === 'choose' && selectedIcon === preset.value ? ' selected' : ''}${mobile ? ' mobile' : ''}`}
                        onClick={() => handleSettingChange('choose', preset.value)}
                    >
                        <div className="grid-item-inner">
                            {preset.icon}
                        </div>
                    </div>
                ))}
                
                {icon && (
                    <div 
                        className={`grid-item${iconSetting === 'workspace' ? ' selected' : ''}${mobile ? ' mobile' : ''}`}
                        onClick={() => handleSettingChange('workspace')}
                    >
                        <div className="grid-item-inner">
                            <img src={icon} alt="Workspace Icon" className="grid-icon" />
                        </div>
                    </div>
                )}

                <div 
                    className={`grid-item${iconSetting === 'upload' ? ' selected' : ''}${mobile ? ' mobile' : ''}`}
                    onClick={() => {
                        if (customIconUrl) {
                            handleSettingChange('upload');
                        } else {
                            openFilePicker();
                        }
                    }}
                    onMouseEnter={() => setHovering(true)}
                    onMouseLeave={() => setHovering(false)}
                >
                    <div className="grid-item-inner">
                        {customIconUrl ? (
                            hovering && customIconUrl !== config?.workspaceStyles?.customIcon ? (
                                <button className="channel-card-delete-button" onClick={(e) => {
                                    e.stopPropagation();
                                    deleteIcon();
                                }}>
                                    <LuTrash2 className="grid-icon" />
                                </button>
                            ) : (
                                <img src={customIconUrl} alt='Custom' className="grid-icon" />
                            )
                        ) : (
                            <>
                                <FaUpload className="grid-icon" style={{ marginBottom: '10px' }} />
                                <span style={{ fontSize: '0.9em', fontWeight: '700', opacity: '0.8', marginBottom: 'auto', textTransform: 'uppercase' }}>{t('Upload Icon')}</span>
                            </>
                        )}
                    </div>
                </div>

                {includeOffOption && (
                    <div 
                        className={`grid-item${iconSetting === 'off' ? ' selected' : ''}${mobile ? ' mobile' : ''}`}
                        onClick={() => handleSettingChange('off')}
                    >
                        <div className="grid-item-inner">
                            <GoCircleSlash className="grid-icon" style={{ marginBottom: '10px' }} />
                            <span style={{ fontSize: '0.9em', fontWeight: '700', opacity: '0.8', marginBottom: 'auto', textTransform: 'uppercase' }}>{t('No Icon')}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};